import {
  Badge,
  Button,
  Container,
  KeyValuePairs,
  KeyValuePairsProps,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { AccountingEvent, AccountingEventsGroup } from "../../types/events";
import { StageContext } from "../../contexes/OrderIdContext";
import {
  getEMPSubsOrchestrationPlatform,
  getFulfillmentState,
  getPaymentState,
  getRefundState,
  hasSettlementDetails,
  isTvodAccountingEvent,
} from "../../helpers/AccountingEventsUtils";
import { screamingSnakeToTitleCase } from "../../helpers/dbdUtils";
import DigitalOrderSourceLink from "../common/DigitalOrderSourceLink";
import { PromotionStatus } from "../common/PromotionStatus";
import { TombstoneList } from "../common/TombstoneList";
import Item = KeyValuePairsProps.Item;

interface AccountingEventsSummarySectionProps {
  accountingGroup: AccountingEventsGroup;
  event: AccountingEvent;
}

export function AccountingEventsSummarySection({
  accountingGroup,
  event,
}: AccountingEventsSummarySectionProps) {
  const stage = useContext(StageContext);
  const [, setSearchParams] = useSearchParams();
  const fulfillmentState = getFulfillmentState(event);
  const paymentState = getPaymentState(event);
  const refundState = getRefundState(event);
  const subscriptionId = event.subscriptionId || "";
  const subscriptionPeriodLabel = event.subscriptionPeriod || "";
  const subscriptionPeriod =
    subscriptionPeriodLabel && /^\d+$/.test(subscriptionPeriodLabel)
      ? Number(subscriptionPeriodLabel)
      : null;
  const canNavigateSubscriptionPeriods =
    !!subscriptionId && subscriptionPeriod !== null;

  const navigateToSubscriptionPeriod = (period: number) => {
    setSearchParams({
      stage,
      transactionId: `${subscriptionId}:${period}`,
    });
  };

  const items: Item[] = [
    {
      label: "Created",
      value: (
        <SpaceBetween direction="horizontal" size="xs">
          {accountingGroup.creationDate
            ? new Date(accountingGroup.creationDate).toLocaleString()
            : "Unknown"}
          {accountingGroup.isRedrive && (
            <Badge color="grey">Manual Redrive</Badge>
          )}
        </SpaceBetween>
      ),
    },
    ...(!isTvodAccountingEvent(event)
      ? [
          {
            label: "Subs ID",
            value: (
              <SpaceBetween direction="vertical" size="xs">
                <span key="subscription-id">
                  {`${subscriptionId}:${subscriptionPeriodLabel}`}
                </span>
                {canNavigateSubscriptionPeriods && (
                  <SpaceBetween
                    key="period-navigation"
                    direction="horizontal"
                    size="xs"
                  >
                    <Button
                      key="previous-period"
                      disabled={subscriptionPeriod === 0}
                      onClick={() =>
                        navigateToSubscriptionPeriod(subscriptionPeriod - 1)
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      key="next-period"
                      onClick={() =>
                        navigateToSubscriptionPeriod(subscriptionPeriod + 1)
                      }
                    >
                      Next
                    </Button>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            ),
          },
        ]
      : []),
    {
      label: "Fulfillment Type",
      value: screamingSnakeToTitleCase(event.fulfillmentType || "IMMEDIATE"),
    },
    {
      label: "Fulfillment Status",
      value: (
        <StatusIndicator type={fulfillmentState.status}>
          {fulfillmentState.text}{" "}
          {event.cancellationReason && `- (${event.cancellationReason})`}
        </StatusIndicator>
      ),
    },
    {
      label: "Payment Status",
      value: (
        <StatusIndicator type={paymentState.status}>
          {paymentState.text}
        </StatusIndicator>
      ),
    },
    {
      label: "Promotions",
      value: event ? (
        <PromotionStatus
          promotionId={event.discountsOnRevenue?.[0]?.promotionId}
        />
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Refund Status",
      value: event ? (
        <StatusIndicator type={refundState.status}>
          {refundState.text}
        </StatusIndicator>
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Settlement Details",
      value: (
        <StatusIndicator
          type={
            hasSettlementDetails(accountingGroup.accountingEvents)
              ? "success"
              : "stopped"
          }
        >
          {hasSettlementDetails(accountingGroup.accountingEvents)
            ? "Has Settlement Details"
            : "No Settlement Details"}
        </StatusIndicator>
      ),
    },
    {
      label: "Digital Order Source",
      value: (
        <DigitalOrderSourceLink
          source={event.digitalOrderSource}
          event={event}
        />
      ),
    },
    {
      label: "Tombstones",
      value: <TombstoneList tombstones={accountingGroup.tombstones ?? []} />,
    },
  ];

  if (event.isEmpOrder && !isTvodAccountingEvent(event)) {
    items.push({
      label: "EMP Subscription Orchestration Platform",
      value: getEMPSubsOrchestrationPlatform(event),
    });
  }

  return (
    <Container>
      <KeyValuePairs columns={2} items={items} />
    </Container>
  );
}
